import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { AutoCompleteData, Branch, BranchAndLocationsAPI, BranchImpl, Customer, CustomerType, LPLocation } from '../types';

@Injectable({
  providedIn: 'root',
})
export class PanoptycAdminService {
  api2Host = `${environment.apiBaseUrl}`;

  private httpClient = inject(HttpClient);

  constructor() {}

  getCompatibleCustomerTypes(branchId: number | string): Observable<CustomerType[]> {
    const requestUrl = `${this.api2Host}/AdminBranch/GetCompatibleCustomerTypes/${branchId}`;
    return this.httpClient.get<CustomerType[]>(requestUrl);
  }

  reclassifyBranch(branchId: number, customerType: string): Observable<BranchAndLocationsAPI> {
    const requestUrl = `${this.api2Host}/AdminBranch/ReclassifyBranch`;
    return this.httpClient.post<BranchAndLocationsAPI>(requestUrl, {
      branch_id: branchId,
      customer_type: customerType,
    });
  }

  getBranchById(id: string): Observable<BranchAndLocationsAPI[]> {
    return this.httpClient.get<BranchAndLocationsAPI[]>(`${this.api2Host}/AdminBranch/GetBranchesAndLocations/${id}`);
  }

  updateFaceRecognition(branchId, faceRecognitionEnabled): Observable<any> {
    const requestUrl = `${this.api2Host}/AdminBranch/FaceRecognition/${branchId}`;
    return this.httpClient.put<any>(requestUrl, { face_recognition_enabled: faceRecognitionEnabled });
  }

  updateEmailOptOut(branchId: number, optout: boolean): Observable<any> {
    const requestUrl = `${this.api2Host}/AdminBranch/EmailOptOut/${branchId}`;
    return this.httpClient.put<any>(requestUrl, { email_opt_out: optout });
  }

  getAutoCompleteData(): Observable<AutoCompleteData[]> {
    const requestUrl = `${this.api2Host}/Admin/Autocomplete`;
    return this.httpClient.get<AutoCompleteData[]>(requestUrl);
  }

  getCustomerTypes(): Observable<CustomerType[]> {
    const requestUrl = `${this.api2Host}/CustomerPortal/GetCustomerTypes`;
    return this.httpClient.get<CustomerType[]>(requestUrl);
  }

  addOrgUUID(data): Observable<any> {
    const requestUrl = `${this.api2Host}/Admin/OrgUuid`;
    return this.httpClient.post<any>(requestUrl, data);
  }

  renameBranch(id: number, name: string) {
    return this.httpClient.post<void>(`${this.api2Host}/AdminBranch/Rename/${id}`, {
      new_name: name,
    });
  }

  deleteBranch(id: number) {
    const requestUrl = `${this.api2Host}/AdminBranch/Delete`;
    return this.httpClient.post<any>(requestUrl, id);
  }

  splitBranch(source_branch_id: number, new_branch_name: string | undefined, location_ids: number[], target_branch_id?: number) {
    const href = `${this.api2Host}/AdminBranch/Split`;
    const body = { source_branch_id, new_branch_name, target_branch_id, location_ids };
    return this.httpClient.post(href, body);
  }

  mergeBranch(from_branch_id: number, to_branch_id: number) {
    const href = `${this.api2Host}/AdminBranch/Merge`;
    const body = { from_branch_id, to_branch_id };
    return this.httpClient.post(href, body);
  }

  uploadTransactionsFile(files: Array<File>) {
    const formData = new FormData();
    for (const file of files) {
      formData.append(`files`, file);
    }
    const requestUrl = `${this.api2Host}/Admin/UploadTransactions`;
    return this.httpClient.post<any>(requestUrl, formData);
  }

  deleteTransactions(upload_ids: string[]) {
    const requestUrl = `${this.api2Host}/Admin/Transactions`;
    return this.httpClient.delete<any>(requestUrl, { params: { upload_ids } });
  }

  setUploadBranchAlias(transaction_upload_id: string, branch_alias: string) {
    const requestUrl = `${this.api2Host}/Admin/SetUploadBranchAlias`;
    return this.httpClient.post<any>(requestUrl, { transaction_upload_id, branch_alias });
  }

  retryUpload(transaction_upload_id) {
    const requestUrl = `${this.api2Host}/Admin/RetryUpload`;
    return this.httpClient.post<any>(requestUrl, { transaction_upload_id });
  }

  getLocationsAutocomplete(filterValue: string) {
    const requestUrl = `${this.api2Host}/AdminLocation/LocationAutocomplete/${filterValue}`;
    return this.httpClient.get<LPLocation[]>(requestUrl);
  }

  public createBranchesFromAPIResponse(data: BranchAndLocationsAPI[]): Map<number, BranchImpl> {
    const branches: Map<number, BranchImpl> = new Map<number, BranchImpl>();
    data.forEach((d) => {
      const {
        branch,
        branch_id,
        face_recognition_enabled,
        keystore,
        branch_alias,
        branch_alias_id,
        data_source,
        location,
        location_id,
        location_alias,
        location_alias_id,
        location_attributes,
        scrape,
        active,
        stripe_customer_id,
        customer_id,
        customer_type,
        stripe_price_id,
        trial_end,
        flagged,
        flagged_reasons,
        flagged_at,
        status,
        assigned_cameras,
        lp_queue,
      } = d;

      let b: BranchImpl = null;
      if (branches.has(d.branch_id)) {
        b = branches.get(d.branch_id);
      } else {
        b = new BranchImpl({
          id: branch_id,
          name: branch,
          faceRecognitionEnabled: face_recognition_enabled,
          keystore: keystore,
          stripeCustomerId: stripe_customer_id,
          customerType: customer_type,
          customerId: customer_id,
          lp_queue: lp_queue,
        });

        if (b.keystore) {
          try {
            b.keystore = JSON.parse(b.keystore as string);
          } catch {
            /* we do not care about this failure */
          }
        }

        b.keystore ??= {};
        b.keystore.theft_report_options ??= {};

        branches.set(branch_id, b);
      }

      if (branch_alias_id) {
        b.addBranchAlias(branch_alias_id, branch_alias, data_source);
      }

      if (location_id) {
        const l = b.addLocation({
          id: location_id,
          branchId: branch_id,
          name: location,
          stripePriceId: stripe_price_id,
          trialEnd: trial_end ? new Date(trial_end) : null,
          flaggedReasons: flagged_reasons,
          flaggedAt: flagged_at,
          flagged,
          status,
          assigned_cameras,
          attributes: location_attributes,
        });
        if (location_alias_id) {
          l.addLocationAlias(location_alias_id, branch_alias_id, location_alias, data_source, active, scrape);
        }
      }
    });

    return branches;
  }

  public createBranchFromAPIResponse(data: BranchAndLocationsAPI[], branchId: number): BranchImpl {
    const branches = this.createBranchesFromAPIResponse(data);
    return branches.get(branchId);
  }

  public getCustomers(): Observable<Customer[]> {
    const requestUrl = `${this.api2Host}/Admin/Customers`;
    return this.httpClient.get<Customer[]>(requestUrl);
  }

  public createCustomer(name: string): Observable<Customer> {
    const requestUrl = `${this.api2Host}/Admin/Customers`;
    return this.httpClient.post<Customer>(requestUrl, { name });
  }

  public removeCustomer(customerId: string): Observable<any> {
    const requestUrl = `${this.api2Host}/Admin/Customers/${customerId}`;
    return this.httpClient.delete<void>(requestUrl);
  }

  public updateBranchCustomer(branchId: number, customerId: string): Observable<Branch> {
    const requestUrl = `${this.api2Host}/AdminBranch/Branch/${branchId}/Customer`;
    return this.httpClient.patch<Branch>(requestUrl, { customer_id: customerId });
  }
}
